import React from "react";
import Footer from "./Footer";
import NavbarWithPushContent from "./NavbarWithPushContent";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, EffectFade, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import Navbar from "./Navbar";
import DosageForms from "./Dosage";
import NewSimilarProducts from "./NewSimilar";
import { useTranslation } from "react-i18next";

type ProductCategory = {
  _id: string;
  name: string;
  subcategories: { name: string; products: any[] }[];
  products: any[];
};

interface HomeProps {
  products: ProductCategory[];
  lang: string;
}

const sliderImages = [
{
    src: "/0/zgrada_1_50.jpg",
    alt: "slider.0.alt", // Using i18n key for dynamic language
    text: "slider.0.text", // Using i18n key for dynamic language
  },
  {
    src: "0/otc_1_50.jpg",
    alt: "slider.1.alt",
    text: "slider.1.text",
  },
  {
    src: "0/cbd_50.jpg",
    alt: "slider.2.alt",
    text: "slider.2.text",
  },
  {
    src: "0/herbal_1_50.jpg",
    alt: "slider.3.alt",
    text: "slider.3.text",
  },
  {
    src: "0/dietary_1_50.jpg",
    alt: "slider.4.alt",
    text: "slider.4.text",
  },
  {
    src: "0/cosmetic_50.jpg",
    alt: "slider.5.alt",
    text: "slider.5.text",
  },
];

const Home: React.FC<HomeProps> = ({ products, lang }) => {
    const { t } = useTranslation(); // i18n hook to access translations

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <div className="hidden md:block">
        <NavbarWithPushContent products={products} lang={lang}/>
      </div>

      <div className="block md:hidden">
        <Navbar products={products} lang={lang} />
      </div>

      <div className="relative flex-grow">
        <Swiper
          modules={[Navigation, Pagination, EffectFade, Autoplay]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          speed={1200}
          loop={true}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          navigation={{
            nextEl: ".custom-swiper-button-next",
            prevEl: ".custom-swiper-button-prev",
          }}
          pagination={{ clickable: true }}
          className="hero-swiper relative w-full h-[78vh] sm:h-[88vh]"
        >
          {sliderImages.map((image, index) => (
            <SwiperSlide key={index}>
              <div className="relative w-full h-full overflow-hidden">
                {/* Full-screen image with subtle Ken Burns zoom on the active slide */}
                <img
                  src={image.src}
                  alt={t(image.alt)}
                  className="w-full h-full object-cover"
                />

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10" />

                {/* Text content */}
                <div className="absolute inset-0 flex items-center">
                  <div className="hero-content max-w-7xl mx-auto w-full px-5 sm:px-10 lg:px-16">
                    <div className="max-w-md sm:max-w-xl text-white">
                      <span className="block h-1 w-12 sm:w-16 bg-brand-400 rounded-full mb-4 sm:mb-5" />
                      <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3 sm:mb-4 drop-shadow-lg">
                        {t(image.alt)}
                      </h2>
                      <p className="text-sm sm:text-lg lg:text-xl text-gray-100/90 leading-relaxed drop-shadow-md">
                        {t(image.text)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}

          {/* Custom navigation arrows */}
          <button
            aria-label="Next slide"
            className="custom-swiper-button-next hidden sm:flex items-center justify-center absolute right-8 top-1/2 -translate-y-1/2 z-10 cursor-pointer p-4 bg-white/15 hover:bg-brand-400 backdrop-blur-sm border border-white/30 rounded-full transition-all duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 sm:h-7 sm:w-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <button
            aria-label="Previous slide"
            className="custom-swiper-button-prev hidden sm:flex items-center justify-center absolute left-8 top-1/2 -translate-y-1/2 z-10 cursor-pointer p-4 bg-white/15 hover:bg-brand-400 backdrop-blur-sm border border-white/30 rounded-full transition-all duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 sm:h-7 sm:w-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </Swiper>

      </div>
      <NewSimilarProducts lang={lang} />
      <DosageForms lang={lang} />
      <Footer />
    </div>
  );
};

export default Home;
